/**
 * NameSheet — Required naming step before saving a verdict.
 *
 * Slides up OVER the verdict card. Name is required (no skip).
 * Duplicate detection against existing titles (case-insensitive).
 * Keyboard-aware: glues to the top of the OS keyboard via direct DOM writes
 * (bypasses React batching to eliminate the chase/bounce effect).
 *
 * V2: Confirmation state — after save, morphs into a hero price moment
 * showing the asking price that was just locked in. Dismisses automatically
 * after 1.5s. Firestore persistence runs in parallel.
 *
 * All inline styles per project convention.
 */

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { T } from '@/styles/tokens';

interface NameSheetProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (title: string) => void;
  existingTitles: string[];
  medium: string;
  width: number;
  height: number;
  defaultName?: string;
  askingPrice?: number | null; // cents — gutPrice from verdict
}

function formatPrice(cents: number): string {
  const dollars = cents / 100;
  if (Number.isInteger(dollars)) {
    return `$${dollars.toLocaleString()}`;
  }
  return `$${dollars.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

const CONFIRM_HOLD_MS = 1500;

export function NameSheet({
  isOpen,
  onClose,
  onSave,
  existingTitles,
  medium: _medium,
  width: _width,
  height: _height,
  defaultName,
  askingPrice,
}: NameSheetProps) {
  const [name, setName] = useState('');
  const [phase, setPhase] = useState<'naming' | 'confirmed'>('naming');
  const [confirmedTitle, setConfirmedTitle] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);
  const confirmTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isDuplicate =
    name.trim().length > 0 &&
    existingTitles.some(
      (t) => t.trim().toLowerCase() === name.trim().toLowerCase()
    );

  const isValid = name.trim().length > 0 && !isDuplicate;

  // ── Glue sheet to keyboard via direct DOM writes ──────────────────────────
  useLayoutEffect(() => {
    if (!isOpen) return;
    const sheet = sheetRef.current;
    if (!sheet) return;
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const kh = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
      sheet.style.bottom = `${kh}px`;
      sheet.style.maxHeight = `calc(100dvh - ${kh}px - 24px)`;
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    update();

    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      sheet.style.bottom = '';
      sheet.style.maxHeight = '';
    };
  }, [isOpen]);

  // Reset state and auto-focus when opening
  useEffect(() => {
    if (isOpen) {
      setName(defaultName ?? '');
      setPhase('naming');
      setConfirmedTitle('');
      wasOpenRef.current = true;
      const timer = setTimeout(() => inputRef.current?.focus(), 400);
      return () => clearTimeout(timer);
    }
  }, [isOpen, defaultName]);

  // Cleanup confirm timer on unmount
  useEffect(() => {
    return () => {
      if (confirmTimerRef.current) clearTimeout(confirmTimerRef.current);
    };
  }, []);

  // Lock background scroll while sheet is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen && !wasOpenRef.current) return null;

  const handleSave = () => {
    if (!isValid) return;
    const trimmed = name.trim();

    // Blur the keyboard before transitioning
    inputRef.current?.blur();

    // 1. Transition to confirmation state
    setConfirmedTitle(trimmed);
    setPhase('confirmed');

    // 2. Trigger Firestore save immediately (runs in parallel)
    onSave(trimmed);

    // 3. Auto-dismiss after hold duration
    confirmTimerRef.current = setTimeout(() => {
      onClose();
    }, CONFIRM_HOLD_MS);
  };

  // ── Confirmation state ────────────────────────────────────────────
  if (phase === 'confirmed') {
    return (
      <>
        <style>{`
          @keyframes nsBackdropIn  { from { opacity: 0; } to { opacity: 1; } }
          @keyframes nsConfirmFade { from { opacity: 0; transform: scale(0.96); } to { opacity: 1; transform: scale(1); } }
          @keyframes nsCheckDraw {
            from { stroke-dashoffset: 24; }
            to { stroke-dashoffset: 0; }
          }
        `}</style>

        {/* Backdrop — no click-to-close during confirmation */}
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(26,26,26,0.15)',
            zIndex: 130,
            animation: `nsBackdropIn 200ms ease forwards`,
            pointerEvents: 'auto',
          }}
        />

        {/* Confirmation sheet */}
        <div
          ref={sheetRef}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 131,
            backgroundColor: T.white,
            borderRadius: '20px 20px 0 0',
            boxShadow: '0 -4px 20px rgba(26,26,26,0.15)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: `${T['3xl']} ${T['2xl']}`,
            paddingBottom: `max(${T['3xl']}, env(safe-area-inset-bottom, ${T.xl}))`,
            animation: `nsConfirmFade 300ms ${T.easeOut} forwards`,
          }}
        >
          {/* Drag handle (visual consistency) */}
          <div
            style={{
              position: 'absolute',
              top: 12,
              left: '50%',
              transform: 'translateX(-50%)',
              width: 36,
              height: 4,
              borderRadius: 2,
              backgroundColor: T.border,
            }}
          />

          {/* Checkmark circle */}
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
              backgroundColor: '#EDF8F7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: 20,
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke={T.primary}
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline
                points="20 6 9 17 4 12"
                style={{
                  strokeDasharray: 24,
                  strokeDashoffset: 0,
                  animation: 'nsCheckDraw 400ms ease-out 100ms both',
                }}
              />
            </svg>
          </div>

          {/* Asking price — hero moment */}
          {askingPrice != null && askingPrice > 0 && (
            <p
              style={{
                fontFamily: "'Manrope', 'Satoshi', 'DM Sans', sans-serif",
                fontSize: 44,
                fontWeight: 800,
                color: T.text,
                letterSpacing: -2,
                lineHeight: 1,
                margin: '0 0 8px',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatPrice(askingPrice)}
            </p>
          )}

          {/* Title */}
          <p
            style={{
              fontFamily: T.fontFamily,
              fontSize: 15,
              fontWeight: 600,
              color: T.text,
              margin: '0 0 4px',
              textAlign: 'center',
            }}
          >
            {confirmedTitle}
          </p>

          {/* Status line */}
          <p
            style={{
              fontFamily: T.fontFamily,
              fontSize: 13,
              fontWeight: 400,
              color: T.textMuted,
              margin: 0,
              textAlign: 'center',
            }}
          >
            Saved to portfolio
          </p>
        </div>
      </>
    );
  }

  // ── Naming state (original) ───────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes nsBackdropIn  { from { opacity: 0; } to { opacity: 1; } }
        @keyframes nsBackdropOut { from { opacity: 1; } to { opacity: 0; } }
        @keyframes nsSlideUp   { from { transform: translateY(100%); } to { transform: translateY(0); } }
        @keyframes nsSlideDown { from { transform: translateY(0); }   to { transform: translateY(100%); } }
      `}</style>

      {/* Backdrop — must be above VerdictOverlay (z-index: 100) */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(26,26,26,0.15)',
          zIndex: 110,
          animation: isOpen
            ? 'nsBackdropIn 200ms ease forwards'
            : 'nsBackdropOut 200ms ease forwards',
          pointerEvents: isOpen ? 'auto' : 'none',
        }}
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 120,
          backgroundColor: T.white,
          borderRadius: '20px 20px 0 0',
          boxShadow: '0 -4px 20px rgba(26,26,26,0.15)',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: `calc(100dvh - ${T['2xl']})`,
          animation: isOpen
            ? `nsSlideUp 350ms ${T.easeOut} forwards`
            : 'nsSlideDown 250ms ease-in forwards',
        }}
      >
        {/* Scrollable content */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: `0 ${T.xl}` }}>
          {/* Drag handle */}
          <div
            style={{
              padding: `${T.md} 0 ${T.xl}`,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <div
              style={{ width: 36, height: 4, borderRadius: 2, backgroundColor: T.border }}
            />
          </div>

          {/* Title */}
          <p
            style={{
              fontSize: 18,
              fontWeight: 600,
              color: T.text,
              margin: `0 0 ${T.xs}`,
              fontFamily: T.fontFamily,
            }}
          >
            Name this piece
          </p>

          {/* Subtitle */}
          <p
            style={{
              fontSize: 13,
              fontWeight: 400,
              color: T.textSecondary,
              margin: `0 0 ${T.lg}`,
              fontFamily: T.fontFamily,
            }}
          >
            Give your artwork a name to save.
          </p>

          {/* Input */}
          <div style={{ position: 'relative', marginBottom: isDuplicate ? 4 : 12 }}>
            <input
              ref={inputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Blue Period Study"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSave();
              }}
              style={{
                width: '100%',
                height: 48,
                padding: `14px 40px 14px ${T.lg}`,
                fontSize: 16,
                fontFamily: T.fontFamily,
                color: T.text,
                backgroundColor: T.white,
                border: `1.5px solid ${
                  isDuplicate ? '#D97B3D' : name.trim() ? T.primary : T.border
                }`,
                borderRadius: T.radiusMd,
                outline: 'none',
                boxSizing: 'border-box',
                transition: 'border-color 200ms ease',
              }}
            />
            {name.length > 0 && !isDuplicate && (
              <button
                onClick={() => setName('')}
                aria-label="Clear"
                style={{
                  position: 'absolute',
                  right: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  width: 44,
                  height: 44,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  color: '#B8B5AD',
                  fontSize: 18,
                }}
              >
                ×
              </button>
            )}
          </div>

          {/* Duplicate error */}
          {isDuplicate && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
                margin: '0 0 12px 2px',
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#D97B3D"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              <p
                style={{
                  fontSize: 12,
                  fontWeight: 400,
                  color: '#D97B3D',
                  margin: 0,
                  fontFamily: T.fontFamily,
                }}
              >
                You already have a piece with this name
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            flexShrink: 0,
            padding: `${T.md} ${T.xl}`,
            paddingBottom: `max(${T.xl}, env(safe-area-inset-bottom, ${T.md}))`,
            backgroundColor: T.white,
            borderTop: '1px solid rgba(0,0,0,0.05)',
          }}
        >
          <button
            onClick={handleSave}
            disabled={!isValid}
            style={{
              width: '100%',
              height: 52,
              background: isValid ? T.primary : T.border,
              color: isValid ? T.white : '#B8B5AD',
              border: 'none',
              borderRadius: 14,
              fontSize: 16,
              fontWeight: 600,
              cursor: isValid ? 'pointer' : 'default',
              fontFamily: T.fontFamily,
              transition: 'all 200ms ease',
            }}
          >
            Save to Portfolio
          </button>
        </div>
      </div>
    </>
  );
}
